
import React, { useEffect, useState } from 'react'
import { ChevronDown, Plus, X, Clock, AlertCircle, CheckCircle2, Trash2, Edit2 } from 'lucide-react'
import styles from './Calendar.module.css'
import { calendarService } from '../services/calendar'
import { notificationService, PREDEFINED_TASKS } from '../services/notifications'

const TASK_TYPES = {
    daily: { name: 'Hværdagopgaver', color: '#7c898b', bg: '#f6f8f9', strongBg: '#e0e3e6' },
    health: { name: 'Helbredsopdatering', color: '#24a148', bg: '#ecf8f1', strongBg: '#b6f2d2' },
    medication: { name: 'Medicin', color: '#d97706', bg: '#fffbeb', strongBg: '#ffe6b3' },
    meal: { name: 'Mad', color: '#7c3aed', bg: '#f5f3ff', strongBg: '#e3d6fa' }
}


function formatDate(d) {
    if (!d) return ''
    return new Date(d).toLocaleString()
}

export default function Calendar() {
    const [tasks, setTasks] = useState([])
    const [notificationSettings, setNotificationSettings] = useState({
        enabledTasks: [],
        notificationTiming: 'on-time',
        taskSettings: {}
    })
    const [title, setTitle] = useState('')
    const [description, setDescription] = useState('')
    const [startAt, setStartAt] = useState('')
    const [repeat, setRepeat] = useState('NONE')
    const [selectedType, setSelectedType] = useState('daily')
    const [expandedTask, setExpandedTask] = useState(null)
    const [editingTime, setEditingTime] = useState('')
    const [deleteOption, setDeleteOption] = useState('this') // 'this' or 'all'
    const [predefinedTasks, setPredefinedTasks] = useState([...PREDEFINED_TASKS.user])
    const [newTemplateName, setNewTemplateName] = useState('')
    const [newTemplateDescription, setNewTemplateDescription] = useState('')
    const [activeTab, setActiveTab] = useState('custom') // 'custom' or 'templates'

    const userRole = localStorage.getItem('role')
    const isAdmin = userRole === 'admin' || userRole === 'superadmin'

    const today = new Date();
    const [calendarYear, setCalendarYear] = useState(today.getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(today.getMonth());

    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];

    const getMonthGrid = () => {
        const firstDay = new Date(calendarYear, selectedMonth, 1);
        const firstDayWeekIndex = (firstDay.getDay() + 6) % 7; // Shift so Monday=0
        const daysInMonth = new Date(calendarYear, selectedMonth + 1, 0).getDate();

        const cells = [];
        for (let i = 0; i < firstDayWeekIndex; i++) {
            cells.push(null);
        }
        for (let d = 1; d <= daysInMonth; d++) {
            cells.push(new Date(calendarYear, selectedMonth, d));
        }
        return cells;
    };

    const getTasksForDate = (aDate) => {
        if (!aDate) return [];
        return tasks.filter(task => {
            if (!task.startAt) return false;
            const taskDate = new Date(task.startAt);
            return taskDate.toDateString() === aDate.toDateString();
        });
    };

    const selectMonth = (month) => {
        setSelectedMonth(month);
    };

    const changeYear = (delta) => {
        setCalendarYear(prev => prev + delta);
    };

    const refresh = async () => {
        try {
            const data = await calendarService.getTasks()
            setTasks(Array.isArray(data) ? data : [])
        } catch (e) { console.error(e) }
    }

    const loadNotificationSettings = async () => {
        try {
            const settings = await notificationService.getNotificationSettings()
            setNotificationSettings(settings)
        } catch (e) { console.error(e) }
    }

    useEffect(() => {
        refresh()
        loadNotificationSettings()
    }, [])

    const submit = async (e) => {
        e.preventDefault()
        await calendarService.createTask({ title, description, startAt, repeat, type: selectedType })
        setTitle(''); setDescription(''); setStartAt(''); setRepeat('NONE'); setSelectedType('daily')
        await refresh()
    }

    const changeStatus = async (id, status) => {
        await calendarService.updateTask(id, { status })
        await refresh()
    }



    const snooze15 = async (id) => {
        await calendarService.updateTask(id, { status: 'POSTPONED!', snoozeMinutes: 15 })
        await refresh()
    }

    const cancelTask = async (id) => {
        await calendarService.updateTask(id, { status: 'CANCELLED' })
        await refresh()
    }

    const deleteTask = async (id, deleteAll = false) => {
        try {
            if (deleteAll) {
                const task = tasks.find(t => t.id === id)
                if (task) {
                    await calendarService.deleteTask(id)
                }
            } else {
                await calendarService.deleteTask(id)
            }
            setExpandedTask(null)
            await refresh()
        } catch (e) {
            console.error('Delete failed:', e)
        }
    }

    const updateTaskTime = async (id, newTime) => {
        try {
            await calendarService.updateTask(id, { startAt: newTime })
            setExpandedTask(null)
            await refresh()
        } catch (e) {
            console.error('Update failed:', e)
        }
    }

    const addTemplate = () => {
        if (!newTemplateName.trim()) return
        const id = newTemplateName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
        setPredefinedTasks((prev) => [
            ...prev,
            { id: `custom-${Date.now()}-${id}`, name: newTemplateName.trim(), description: newTemplateDescription.trim(), category: 'health', icon: 'Heart' }
        ])
        setNewTemplateName('')
        setNewTemplateDescription('')
    }

    const removeTemplate = (id) => {
        setPredefinedTasks((prev) => prev.filter((task) => task.id !== id))
    }

    const toggleTask = async (taskId, enabled) => {
        try {
            if (enabled) {
                await notificationService.disableTask(taskId)
            } else {
                // Enable with default settings
                await notificationService.enableTask(taskId, {
                    repeat: 'DAILY',
                    notificationTiming: 'on-time'
                })
            }
            await loadNotificationSettings()
        } catch (e) { console.error(e) }
    }

    const updateTaskSettings = async (taskId, settings) => {
        try {
            await notificationService.updateTaskSettings(taskId, settings)
            await loadNotificationSettings()
        } catch (e) { console.error(e) }
    }

    const isTaskEnabled = (taskId) => notificationSettings.enabledTasks?.includes(taskId)
    const getTaskSettings = (taskId) => notificationSettings.taskSettings?.[taskId] || {}

    return (
        <div className="container">
            <h1>Kalender & Påmindelser</h1>

            {/* Month + year selector section */}
            <div className={styles.monthActionBar}>
                <button onClick={() => changeYear(-1)} className={styles.yearBtn}>Forrige år</button>
                <div className={styles.monthList}>
                    {monthNames.map((month, idx) => (
                        <button
                            key={month}
                            className={`${styles.monthItem} ${idx === selectedMonth ? styles.monthActive : ''}`}
                            onClick={() => selectMonth(idx)}
                        >
                            {month}
                        </button>
                    ))}
                </div>
                <button onClick={() => changeYear(1)} className={styles.yearBtn}>Næste år</button>
            </div>

            {/* Tabs */}
            <div className={styles.tabs}>
                <button
                    className={`${styles.tab} ${activeTab === 'custom' ? styles.active : ''}`}
                    onClick={() => setActiveTab('custom')}
                >
                    <Plus size={18} /> Mine opgaver
                </button>
                <button
                    className={`${styles.tab} ${activeTab === 'templates' ? styles.active : ''}`}
                    onClick={() => setActiveTab('templates')}
                >
                    <AlertCircle size={18} /> Påmindelser
                </button>
            </div>

            {/* Custom Tasks Tab */}
            {activeTab === 'custom' && (
                <>
                    <section className={styles.formSection}>
                        <form onSubmit={submit} className={styles.formNew}>
                            <input required placeholder="Opgave titel" value={title} onChange={e => setTitle(e.target.value)} />
                            <input type="datetime-local" required value={startAt} onChange={e => setStartAt(e.target.value)} />

                            <div className={styles.typeSelector}>
                                {Object.entries(TASK_TYPES).map(([key, type]) => (
                                    <button
                                        key={key}
                                        type="button"
                                        className={`${styles.typeBtn} ${selectedType === key ? styles.typeActive : ''}`}
                                        style={{
                                            borderColor: type.color,
                                            color: type.color,
                                            backgroundColor: selectedType === key ? (type.strongBg || type.bg) : 'white',
                                            fontWeight: selectedType === key ? 700 : 500
                                        }}
                                        onClick={() => setSelectedType(key)}
                                    >
                                        {type.name}
                                    </button>
                                ))}
                            </div>

                            <textarea placeholder="Beskrivelse (valgfrit)" value={description} onChange={e => setDescription(e.target.value)} className={styles.descriptionBox} />

                            <select value={repeat} onChange={e => setRepeat(e.target.value)}>
                                <option value="NONE">Ingen gentagelse</option>
                                <option value="DAILY">Hver dag</option>
                                <option value="WEEKLY">Hver uge</option>
                                <option value="MONTHLY">Hver måned</option>
                                <option value="YEARLY">Hvert år</option>
                            </select>
                            <button type="submit">Opret opgave</button>
                        </form>
                    </section>

                    {/* Full month calendar */}
                    <section className={styles.monthCalendar}>
                        <div className={styles.weekRow}>
                            <div>Ma</div><div>Ti</div><div>On</div><div>To</div><div>Fr</div><div>Lø</div><div>Sø</div>
                        </div>
                        <div className={styles.daysGrid}>
                            {getMonthGrid().map((date, idx) => (
                                <div key={idx} className={styles.dayCell}>
                                    {date ? (
                                        <>
                                            <div className={styles.dayLabel}>{date.getDate()}</div>
                                            <div className={styles.dayTasks}>
                                                {getTasksForDate(date).map(task => {
                                                    const typeInfo = TASK_TYPES[task.type] || TASK_TYPES.daily
                                                    return (
                                                        <div
                                                            key={task.id}
                                                            className={styles.calendarTask}
                                                            style={{
                                                                backgroundColor: typeInfo.bg,
                                                                borderColor: typeInfo.color,
                                                                cursor: 'pointer'
                                                            }}
                                                            onClick={() => setExpandedTask(task)}
                                                        >
                                                            {task.title}
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        </>
                                    ) : null}
                                </div>
                            ))}
                        </div>
                    </section>

                    <section className={styles.listSection}>
                        <h2>Dine opgaver</h2>
                        {tasks.length === 0 && <div>Ingen opgaver</div>}
                        <ul className={styles.list}>
                            {tasks.map(t => {
                                const typeInfo = TASK_TYPES[t.type] || TASK_TYPES.daily
                                return (
                                    <li key={t.id} className={styles.item} style={{ borderLeftColor: typeInfo.color, backgroundColor: typeInfo.bg, cursor: 'pointer' }} onClick={() => setExpandedTask(t)}>
                                        <div className={styles.itemContent}>
                                            <div className={styles.itemHeader}>
                                                <div className={styles.title}>{t.title}</div>
                                                <span className={styles.separator}>·</span>
                                                <span className={styles.meta}>Start: {formatDate(t.startAt)}</span>
                                                <span className={styles.separator}>·</span>
                                                <span className={styles.meta}>Gentag: {t.repeat || 'Ingen'}</span>
                                            </div>
                                            {t.description && <div className={styles.description}>{t.description}</div>}
                                        </div>
                                        <div className={styles.actions}>
                                            <button onClick={(e) => { e.stopPropagation(); changeStatus(t.id, 'DONE'); }}>Udført</button>
                                            <button onClick={(e) => { e.stopPropagation(); snooze15(t.id); }}>Udsæt 15 min</button>
                                            <button onClick={(e) => { e.stopPropagation(); cancelTask(t.id); }}>Annuller</button>
                                        </div>
                                    </li>
                                )
                            })}
                        </ul>
                    </section>

                    {/* Task Detail Modal */}
                    {expandedTask && (
                        <div className={styles.modal} onClick={() => setExpandedTask(null)}>
                            <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
                                <div className={styles.modalHeader}>
                                    <h2>{expandedTask.title}</h2>
                                    <button onClick={() => setExpandedTask(null)} className={styles.closeBtn}><X size={20} /></button>
                                </div>
                                <div className={styles.modalBody}>
                                    {expandedTask.description && (
                                        <div>
                                            <label>Beskrivelse:</label>
                                            <p>{expandedTask.description}</p>
                                        </div>
                                    )}
                                    <div>
                                        <label>Type:</label>
                                        <p>{TASK_TYPES[expandedTask.type]?.name || expandedTask.type}</p>
                                    </div>
                                    <div>
                                        <label>Gentagelse:</label>
                                        <p>{expandedTask.repeat || 'Ingen'}</p>
                                    </div>
                                    <div>
                                        <label>Tid:</label>
                                        <input type="datetime-local" value={editingTime || expandedTask.startAt?.slice(0, 16)} onChange={e => setEditingTime(e.target.value)} />
                                    </div>
                                    {editingTime && editingTime !== expandedTask.startAt?.slice(0, 16) && (
                                        <div className={styles.deleteOptions}>
                                            <label>
                                                <input type="radio" checked={deleteOption === 'this'} onChange={() => setDeleteOption('this')} />
                                                Kun denne
                                            </label>
                                            {expandedTask.repeat !== 'NONE' && (
                                                <label>
                                                    <input type="radio" checked={deleteOption === 'all'} onChange={() => setDeleteOption('all')} />
                                                    Alle fremtidige
                                                </label>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className={styles.modalActions}>
                                    {editingTime && editingTime !== expandedTask.startAt?.slice(0, 16) && (
                                        <button onClick={() => { updateTaskTime(expandedTask.id, editingTime); setEditingTime(''); }}>Gem tidspunkt</button>
                                    )}
                                    <button className={styles.deleteBtn} onClick={() => deleteTask(expandedTask.id, deleteOption === 'all')}>Slet opgave</button>
                                    <button onClick={() => setExpandedTask(null)}>Luk</button>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Templates Tab */}
            {activeTab === 'templates' && (
                <section className={styles.templatesSection}>
                    {/* Admin quick edit for templates */}
                    {isAdmin && (
                        <div className={styles.templateCategory}>
                            <h2>Admin: Rediger påmindelser</h2>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                                <input
                                    placeholder="Ny påmindelse titel"
                                    value={newTemplateName}
                                    onChange={(e) => setNewTemplateName(e.target.value)}
                                />
                                <input
                                    placeholder="Beskrivelse"
                                    value={newTemplateDescription}
                                    onChange={(e) => setNewTemplateDescription(e.target.value)}
                                />
                            </div>
                            <button onClick={addTemplate} style={{ width: '100%' }}>Tilføj påmindelse</button>
                        </div>
                    )}

                    <div className={styles.templateCategory}>
                        <h2>Mine påmindelser</h2>
                        <p className={styles.categoryDescription}>Tilvælg de påmindelser du ønsker at få</p>
                        {predefinedTasks.map(task => (
                            <div key={task.id} className={styles.templateItem}>
                                <div className={styles.templateHeader}>
                                    <input
                                        type="checkbox"
                                        checked={isTaskEnabled(task.id)}
                                        onChange={(e) => toggleTask(task.id, isTaskEnabled(task.id))}
                                        className={styles.taskCheckbox}
                                    />
                                    <div className={styles.templateInfo}>
                                        <div className={styles.templateName}>{task.name}</div>
                                        <div className={styles.templateDescription}>{task.description}</div>
                                    </div>
                                    {isTaskEnabled(task.id) && (
                                        <button
                                            className={styles.expandBtn}
                                            onClick={() => setExpandedTask(expandedTask === task.id ? null : task.id)}
                                        >
                                            <ChevronDown size={20} style={{ transform: expandedTask === task.id ? 'rotate(180deg)' : '' }} />
                                        </button>
                                    )}
                                    {isAdmin && (
                                        <button
                                            className={styles.expandBtn}
                                            style={{ color: '#b42318' }}
                                            onClick={() => removeTemplate(task.id)}
                                        >
                                            <X size={18} />
                                        </button>
                                    )}
                                </div>

                                {/* Task Settings */}
                                {isTaskEnabled(task.id) && expandedTask === task.id && (
                                    <div className={styles.taskSettings}>
                                        <div className={styles.settingGroup}>
                                            <label>Gentagelse</label>
                                            <select
                                                value={getTaskSettings(task.id).repeat || 'DAILY'}
                                                onChange={(e) => updateTaskSettings(task.id, { repeat: e.target.value })}
                                            >
                                                <option value="DAILY">Hver dag</option>
                                                <option value="WEEKLY">Hver uge</option>
                                                <option value="MONTHLY">Hver måned</option>
                                                <option value="YEARLY">Hvert år</option>
                                            </select>
                                        </div>

                                        <div className={styles.settingGroup}>
                                            <label>Startdato</label>
                                            <input
                                                type="datetime-local"
                                                value={getTaskSettings(task.id).startDate || ''}
                                                onChange={(e) => updateTaskSettings(task.id, { startDate: e.target.value })}
                                            />
                                        </div>

                                        <div className={styles.settingGroup}>
                                            <label>Påmindelses tidspunkt</label>
                                            <select
                                                value={getTaskSettings(task.id).notificationTiming || 'on-time'}
                                                onChange={(e) => updateTaskSettings(task.id, { notificationTiming: e.target.value })}
                                            >
                                                <option value="on-time">Ved det planlagte tidspunkt</option>
                                                <option value="day-before">Dagen før</option>
                                                <option value="week-before">En uge før</option>
                                            </select>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* System-defined tasks (mandatory) */}
                    <div className={styles.templateCategory}>
                        <h2>
                            <AlertCircle size={20} />
                            Vigtige påmindelser (obligatorisk)
                        </h2>
                        <p className={styles.categoryDescription}>Disse påmindelser hjælper dig med vigtige sundhedsmål</p>
                        {PREDEFINED_TASKS.system.map(task => (
                            <div key={task.id} className={`${styles.templateItem} ${styles.mandatory}`}>
                                <div className={styles.templateHeader}>
                                    <div className={styles.mandatoryBadge}>
                                        <CheckCircle2 size={20} />
                                    </div>
                                    <div className={styles.templateInfo}>
                                        <div className={styles.templateName}>{task.name}</div>
                                        <div className={styles.templateDescription}>{task.description}</div>
                                    </div>
                                    <button
                                        className={styles.expandBtn}
                                        onClick={() => setExpandedTask(expandedTask === task.id ? null : task.id)}
                                    >
                                        <ChevronDown size={20} style={{ transform: expandedTask === task.id ? 'rotate(180deg)' : '' }} />
                                    </button>
                                </div>

                                {/* Task Settings */}
                                {expandedTask === task.id && (
                                    <div className={styles.taskSettings}>
                                        <div className={styles.settingGroup}>
                                            <label>Gentagelse</label>
                                            <select
                                                value={getTaskSettings(task.id).repeat || 'YEARLY'}
                                                onChange={(e) => updateTaskSettings(task.id, { repeat: e.target.value })}
                                            >
                                                <option value="DAILY">Hver dag</option>
                                                <option value="WEEKLY">Hver uge</option>
                                                <option value="MONTHLY">Hver måned</option>
                                                <option value="YEARLY">Hvert år</option>
                                            </select>
                                        </div>

                                        <div className={styles.settingGroup}>
                                            <label>Startdato</label>
                                            <input
                                                type="datetime-local"
                                                value={getTaskSettings(task.id).startDate || ''}
                                                onChange={(e) => updateTaskSettings(task.id, { startDate: e.target.value })}
                                            />
                                        </div>

                                        <div className={styles.settingGroup}>
                                            <label>Påmindelses tidspunkt</label>
                                            <select
                                                value={getTaskSettings(task.id).notificationTiming || 'day-before'}
                                                onChange={(e) => updateTaskSettings(task.id, { notificationTiming: e.target.value })}
                                            >
                                                <option value="on-time">Ved det planlagte tidspunkt</option>
                                                <option value="day-before">Dagen før</option>
                                                <option value="week-before">En uge før</option>
                                            </select>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </section>
            )}
        </div>
    )
}
